import React, { useState } from "react";
import { Modal, Row, Col, Button } from "react-bootstrap";
import { MdContentCopy, MdCheck } from "react-icons/md";
import {
  FaFacebook,
  FaTwitter,
  FaWhatsapp,
  FaLinkedin,
  FaPinterest,
} from "react-icons/fa";
import "./ShareModal.css";

const ShareModal = ({ isOpen, onClose, shareUrl, companyData, licenseKey, shareType = "url" }) => {
  const [copied, setCopied] = useState(false);
  const [copiedLicense, setCopiedLicense] = useState(false);

  const handleCopyLink = async () => {
    try {
      if (!shareUrl) return;

      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // fallback for http / older browsers
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed"; // prevent scroll jump
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleCopyLicenseKey = async () => {
    try {
      if (!licenseKey) return;

      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(licenseKey);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = licenseKey;
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      setCopiedLicense(true);
      setTimeout(() => setCopiedLicense(false), 2000);
    } catch (err) {
      console.error("Failed to copy license key: ", err);
    }
  };

  const shareOptions = [
    {
      name: "Facebook",
      icon: FaFacebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl
      )}`,
      color: "#1877f2",
    },
    {
      name: "X (Twitter)",
      icon: FaTwitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}`,
      color: "#000000",
    },
    {
      name: "WhatsApp",
      icon: FaWhatsapp,
      url: `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
      color: "#25d366",
    },
    {
      name: "LinkedIn",
      icon: FaLinkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl
      )}`,
      color: "#0077b5",
    },
    {
      name: "Pinterest",
      icon: FaPinterest,
      url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
        shareUrl
      )}`,
      color: "#bd081c",
    },
  ];

  const handleShare = (url) => {
    window.open(url, "_blank", "width=600,height=400");
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg">
      <Modal.Header style={{ padding: "4px 20px" }} closeButton>
        <Modal.Title className="fs-4">
          Share your cyber risk scan {licenseKey ? "link & license key" : "link"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-4">
          <Col
            xs={3}
            className="text-center d-flex align-items-center justify-content-center"
          >
            <img
              src={
                companyData?.logo ||
                "https://washi.s3.amazonaws.com/RiskReport360.png"
              }
              alt={companyData?.companyName || "Almata"}
              className="tool-logo"
            />
          </Col>
          <Col xs={9} className="text-start">
            <h5>{companyData?.companyName || "almata.com"}</h5>
            <p className="text-muted fs-6">
              <strong>{companyData?.companyName}</strong>, share this unique scan URL with your
              users to help them evaluate their domain security in real time.
              Our AI-powered engine performs a comprehensive external assessment
              identifying exposed <strong>assets</strong>, <strong>weak</strong> <strong>configurations</strong>, and <strong>potential</strong> vulnerabilities then generates a clear, actionable Cyber Risk
              Report. Help your users stay proactive by discovering risks before
              attackers do.
            </p>
          </Col>
        </Row>

        <Row className="ms-3">
          {shareOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <Col key={option.name} className="d-flex flex-column mb-3">
                <Button
                  variant="light"
                  className="share-btn rounded-circle p-2 mb-2"
                  onClick={() => handleShare(option.url)}
                  style={{ color: option.color }}
                >
                  <IconComponent size={40} />
                </Button>
                <div className="small">{option.name}</div>
              </Col>
            );
          })}
          <Col xs={2} className="d-flex flex-column mb-3">
            <Button
              variant="light"
              className="share-btn rounded-circle p-2 mb-2"
              onClick={handleCopyLink}
              style={{
                backgroundColor: copied ? "#28a745" : "",
                color: copied ? "white" : "",
                borderColor: copied ? "#28a745" : "",
              }}
            >
              {copied ? <MdCheck size={20} /> : <MdContentCopy size={20} />}
            </Button>
            <div className="small">{copied ? "Copied!" : "Copy URL"}</div>
          </Col>
          {licenseKey && (
            <Col xs={2} className="d-flex flex-column mb-3">
              <Button
                variant="light"
                className="share-btn rounded-circle p-2 mb-2"
                onClick={handleCopyLicenseKey}
                style={{
                  backgroundColor: copiedLicense ? "#28a745" : "#f8f9fa",
                  color: copiedLicense ? "white" : "#6c757d",
                  borderColor: copiedLicense ? "#28a745" : "#dee2e6",
                }}
              >
                {copiedLicense ? <MdCheck size={20} /> : <MdContentCopy size={20} />}
              </Button>
              <div className="small">{copiedLicense ? "Copied!" : "Copy Key"}</div>
            </Col>
          )}
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ShareModal;
